import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";
import TryDemoCta from "./TryDemoCta";

// Public /demo page. A shareable, indexable URL that explains the demo and carries the
// disclosure copy.
//
// Deliberately NOT an auto-login route: a GET that mints a session would fire on every crawler,
// link-preview bot and browser prefetch, minting sessions nobody asked for. The visitor presses
// a button, which POSTs.

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://gearcheckauto.com/" },
      { "@type": "ListItem", position: 2, name: "Demo", item: "https://gearcheckauto.com/demo" },
    ],
  },
];

const OWNER_STEPS = [
  "Pick a vehicle and describe what is wrong with it.",
  "Send that one request to several nearby shops at once.",
  "Compare the replies and quotes side by side in one inbox.",
];

const SHOP_STEPS = [
  "See the requests coming in from nearby vehicle owners.",
  "Reply with a quote and work out the details by message.",
  "Track the job through the work order states, from quoted to completed.",
];

export default function DemoPage({ onNavigate, onTryDemo }) {
  return (
    <MarketingLayout onNavigate={onNavigate} jsonLd={JSON_LD}>
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white dark:to-gray-800 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Demo</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            Try GearCheckAuto without signing up
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
            Open the real product, loaded with sample shops and conversations, and click around.
            No account, no email address, nothing to cancel.
          </p>
          <TryDemoCta onTryDemo={onTryDemo} className="mt-7" />
        </div>
      </section>

      <section className="px-4 py-16">
        <div className="mx-auto grid max-w-3xl gap-10 sm:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
              As a vehicle owner
            </h2>
            <ul className="mt-4 space-y-3 text-slate-600 dark:text-gray-300">
              {OWNER_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
              As a repair shop
            </h2>
            <ul className="mt-4 space-y-3 text-slate-600 dark:text-gray-300">
              {SHOP_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mx-auto mt-10 max-w-3xl leading-relaxed text-slate-600 dark:text-gray-300">
          You can switch between the two sides while you are in there, so you can send a request as
          an owner, answer it as the shop, and watch it arrive back. There is a longer walkthrough
          on the{" "}
          <SpaLink view="howItWorks" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:underline">
            How it works page
          </SpaLink>
          .
        </p>
      </section>

      <section className="bg-slate-50 dark:bg-gray-900 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            What to know before you start
          </h2>
          <dl className="mt-8 space-y-6 text-slate-600 dark:text-gray-300">
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">It is shared</dt>
              <dd className="mt-1 leading-relaxed">
                Everyone trying the demo right now is in the same account, so you may see messages
                someone else just sent. Please do not enter real personal information.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">It gets wiped</dt>
              <dd className="mt-1 leading-relaxed">
                The demo resets on a schedule, so anything you create there is temporary and will
                not follow you to a real account.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">The shops are not real</dt>
              <dd className="mt-1 leading-relaxed">
                The demo is stocked with sample shops around Minneapolis and St. Paul so the map
                and search have something to show. No real shop ever sees a demo request.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">A few things are switched off</dt>
              <dd className="mt-1 leading-relaxed">
                Billing, notification settings and permanent deletion are disabled in the demo.
                Everything else works exactly as it does in a real account.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="bg-slate-900 px-4 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">Ready when you are</h2>
          <p className="mt-3 leading-relaxed text-slate-300">
            Free for vehicle owners. Create a real account whenever you want to keep what you send.
          </p>
          <TryDemoCta onTryDemo={onTryDemo} align="center" className="mt-7" />
          <button
            type="button"
            onClick={() => onNavigate("signup")}
            className="mt-6 rounded-lg border border-slate-500 px-7 py-3 text-base font-semibold text-white hover:bg-slate-800"
          >
            Create a free account
          </button>
        </div>
      </section>
    </MarketingLayout>
  );
}
